import { Lang } from '#~/control/index.js';
import { Sel, STag } from '#~/stag/tag.js';
import { None } from './constants.js';
import type { Props } from './types.js';
import * as Select from '@studio/design-system/select';
import { Protocol as M_Protocol, toProtocol } from '@studio/models/control';
import { useLatestFn, useLazyRef } from '@studio/react-utils/hooks';
import { type ReactElement, useState } from 'react';

/** Video `<Protocol>` component. */
export const Protocol = ({ placeholder, protocol, size, ...props }: Props): ReactElement => {
  // The store keeps No Control as `undefined`, the same as "not chosen yet", so
  // the component tracks on its own whether the user has made a choice. Only
  // this flag lets a chosen No Control display as "No Control" rather than
  // falling back to the placeholder. A remount (for example, stepping back
  // through the wizard) loses the flag and shows the placeholder again: the
  // store cannot distinguish "unset" from "chose none", which is its existing
  // contract.
  const [chosen, setChosen] = useState(false);

  const handleValueChange = useLatestFn((nextValue: string) => {
    const nextProtocol = nextValue !== None.value ? toProtocol(nextValue) : undefined;

    setChosen(true);

    if (nextProtocol !== protocol.value) {
      protocol(nextProtocol);
    }
  });

  return (
    <Select.Root
      onValueChange={handleValueChange}
      size={size}
      // With a placeholder, an unset protocol reads as "not chosen yet" ('' makes
      // Radix render the placeholder) instead of a pre-picked No Control, until
      // the user picks something. Without a placeholder, unset is No Control.
      value={protocol.value ?? (placeholder && !chosen ? '' : None.value)}
    >
      <Select.Trigger {...props} {...STag(Sel.ConfigVideoDeviceControl, 'protocol')}>
        <Select.Value placeholder={placeholder} />
      </Select.Trigger>

      <Select.Content>
        {
          useLazyRef(() => (
            <>
              {/* ENG-4832: the Connect controls step lists every connection
                  type plus No Control. VISCA over Serial already has an editor
                  in the add wizard and in Settings, so it is offered here too. */}
              {...[
                M_Protocol.ONVIF,
                M_Protocol.VISCA_IP,
                M_Protocol.VISCA_SERIAL,
                M_Protocol.HTTP
              ].map((µ) => (
                <Select.Item
                  key={µ}
                  value={µ}
                  {...STag(Sel.ConfigVideoDeviceControl, 'protocol', µ)}
                >
                  {Lang.protocol[µ]}
                </Select.Item>
              ))}
              <Select.Item
                key={None.value}
                value={None.value}
                {...STag(Sel.ConfigVideoDeviceControl, 'protocol', None.value)}
              >
                {None.label}
              </Select.Item>
            </>
          )).current
        }
      </Select.Content>
    </Select.Root>
  );
};
